import { FC, PropsWithChildren, useCallback } from 'react';
import { merge } from 'lodash';

import {
  BffProvider,
  BffProviderProps,
  RequestInterceptorFulfilledFn,
} from '@helpful/network';
import { useDate, useLocale } from '@wearehelpful/ui-localization';
import { useSession } from '@core/auth';
import { useAnalytics } from '@wearehelpful/ui-stats';

export type NetworkProps = PropsWithChildren<{
  bff: BffProviderProps;
}>;

export const Network: FC<NetworkProps> = ({ children, bff }) => {
  const { tz } = useDate();
  const [{ lng }] = useLocale();
  const [, getAccessToken] = useSession();
  const { getAnonymousId } = useAnalytics();

  const requestInterceptorFulfilled: RequestInterceptorFulfilledFn =
    useCallback(
      async (config) => {
        const token = await getAccessToken();

        return merge({}, config, {
          headers: {
            'Time-Zone': tz,
            Language: lng,
            Authorization: token ? `Bearer ${token}` : undefined,
            'X-User-Id': getAnonymousId(),
            'Pixel-Ratio': window.devicePixelRatio,
          },
        });
      },
      [tz, lng, getAccessToken, getAnonymousId]
    );

  return (
    <BffProvider
      {...bff}
      requestInterceptorFulfilled={requestInterceptorFulfilled}
    >
      {children}
    </BffProvider>
  );
};
